<template>
    <div class="mt-5 grid-cols-layout border-b border-gray-300 pb-5 lg:grid">
        <div class="mb-6 lg:mb-0 lg:mt-4">
            <h3
                class="mb-[5px] text-lg font-medium leading-normal tracking-small text-gray-800"
            >
                Two Factor Authentication
            </h3>
            <p class="max-w-xs text-sm text-gray-500">
                Two-Factor Authentication (2FA) can be used to help protect your
                account from unauthorized access by requiring you to enter a
                security code when you sign in.
            </p>
        </div>
        <div class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0">
            <div class="border-b px-6 pb-6">
                <p
                    v-if="!twoFactorEnabled"
                    class="text-md pt-6 leading-normal text-gray-800"
                >
                    Two-Factor Authentication is
                    <span class="font-medium text-topol-red-600"
                        >not enabled</span
                    >
                </p>
                <p v-else class="text-md pt-6 leading-normal text-gray-800">
                    Two-Factor Authentication is
                    <span class="font-medium text-green-600">enabled</span>
                </p>
                <p class="pt-6 text-sm leading-normal text-gray-500">
                    When Two-Factor Authentication (2FA) is enabled, when you
                    sign in you’ll be required to use the security code provided
                    by your Authenticator App of choice. You can learn more
                    about 2FA in our documentation.
                </p>

                <component
                    :is="confirmComponent"
                    v-if="twoFactorEnabled"
                    @confirmed="showRecoveryCodes = true"
                >
                    <AtomicButton class="mt-6" color="white" size="sm">
                        Show recovery codes
                    </AtomicButton>
                </component>
                <component
                    :is="confirmComponent"
                    v-if="twoFactorEnabled"
                    @confirmed="showPairingCodes = true"
                >
                    <AtomicButton class="ml-3 mt-6" color="white" size="sm">
                        Pairing QR code
                    </AtomicButton>
                </component>
            </div>

            <div class="flex justify-end">
                <div class="my-3 pr-6">
                    <component
                        :is="confirmComponent"
                        v-if="!twoFactorEnabled"
                        @confirmed="showTwoFactorAuthentication = true"
                    >
                        <AtomicButton color="yellow" size="sm">
                            Enable 2FA for Sign in
                        </AtomicButton>
                    </component>

                    <component
                        :is="confirmComponent"
                        v-else
                        @confirmed="disableTwoFactorAuthentication"
                    >
                        <AtomicButton
                            color="yellow"
                            size="sm"
                            :class="{ 'opacity-25': loading }"
                            :disabled="loading"
                        >
                            Disable 2FA for Sign in
                        </AtomicButton>
                    </component>
                </div>
            </div>
        </div>

        <TwoFactorAuthenticationModal
            v-if="showTwoFactorAuthentication"
            :has_password="props.has_password"
            @close="showTwoFactorAuthentication = false"
            @enabled="enabled = true"
        />
        <TwoFactorAuthenticationRecoveryCodesModal
            v-if="showRecoveryCodes"
            @close="showRecoveryCodes = false"
        />
        <TwoFactorAuthenticationPairingCodesModal
            v-if="showPairingCodes"
            @close="showPairingCodes = false"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import ConfirmsPassword from './ConfirmsPassword.vue';
import ConfirmsOtp from './ConfirmsOtp.vue';

const globalProps = computed(() => usePage<ITopolPageProps>().props);

const props = defineProps({
    has_password: {
        type: Boolean,
        required: true,
    },
});

const confirmComponent = computed(() =>
    props.has_password ? ConfirmsPassword : ConfirmsOtp
);
const loading = ref(false);
const showRecoveryCodes = ref(false);
const showTwoFactorAuthentication = ref(false);
const showPairingCodes = ref(false);
const enabled = ref(false);

const twoFactorEnabled = computed(() => {
    return globalProps.value.user.two_factor_enabled || enabled.value;
});

const disableTwoFactorAuthentication = () => {
    loading.value = true;
    router.delete('/user/two-factor-authentication', {
        preserveScroll: true,
        onSuccess: () => {
            loading.value = false;
            enabled.value = false;
        },
    });
};
</script>
